import { useMemo } from 'react'
import { Box, Drawer, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PractitionerReportInsightsPanel from 'src/mentor/components/MentorClinicBookingsPage/component/PractitionerReportInsightsPanel'
import { DEFAULT_STATS_API, StatsApiContext } from 'src/shared/stats/statsApiContext'

/**
 * The sub-tabs of the shared insights panel are role-driven. On the mentor portal they come from
 * the LOGGED-IN practitioner's roles; here we derive them from the VIEWED practitioner's
 * KYC-approved services (teacherUnEditableFields.servicesApproved, keyed by ROLE_*), mirroring the
 * backend's isAllowedEmrReport gating so we only offer tabs the admin endpoint will serve.
 */
export function practitionerStatsRolesFromProfile(practitionerProfile) {
  const services = practitionerProfile?.teacherUnEditableFields?.servicesApproved
  const approved = (roleKey) => {
    const detail = services && typeof services === 'object' ? services[roleKey] : null
    if (!detail) return false
    // ServiceDetails.enabled defaults to false on the bean; only count truthy values.
    return detail.enabled !== false
  }
  return {
    isDoctor: approved('ROLE_DOCTOR'),
    isTherapist: approved('ROLE_THERAPIST'),
    isScreener: approved('ROLE_SCREENER'),
  }
}

/**
 * Support-portal "Stats" drawer for a practitioner — renders the EXACT practitioner "My Reports"
 * framework (PractitionerReportInsightsPanel) against the read-only practitioner-stats admin
 * endpoint (needs the explicit mentorId; see backend PractitionerStatsAdminController).
 * Enhancements to the shared panel show up here automatically.
 *
 * Scope: pass brand + clinicLocation for a single-clinic view, or omit BOTH for the cumulative
 * "across all clinics" view (the admin endpoint treats clinic scope as optional).
 *
 * @param practitioner    the viewed practitioner (support by-slug / listing minimal response)
 * @param brand           brand summary ({ id, title, ... }) — optional
 * @param clinicLocation  the clinic location profile ({ id, title, ... }) — optional
 */
export default function SupportPractitionerStatsDrawer({
  open,
  onClose,
  practitioner,
  brand = null,
  clinicLocation = null,
}) {
  const statsApi = useMemo(
    () => ({
      ...DEFAULT_STATS_API,
      practitionerInsightsPath: '/api/secure/cc/support/practitioner-stats/report-insights',
      practitionerInsightsExtraParams: practitioner?.id ? { mentorId: practitioner.id } : null,
      practitionerInsightsRequiresClinicLocation: false,
    }),
    [practitioner?.id],
  )
  const roles = useMemo(() => practitionerStatsRolesFromProfile(practitioner), [practitioner])
  const hasAnyClinicalRole = roles.isDoctor || roles.isTherapist || roles.isScreener

  const practitionerName =
    practitioner?.name ||
    [practitioner?.firstName, practitioner?.lastName].filter(Boolean).join(' ') ||
    'Practitioner'

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: '88%', lg: '78%' }, maxWidth: '1300px' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 5,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: '#418571', fontWeight: 700, letterSpacing: 1 }}>
            Practitioner Stats
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
            {practitionerName}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {[brand?.title, clinicLocation?.title].filter(Boolean).join(' — ') ||
              'All clinics (cumulative)'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close practitioner stats">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {open && practitioner?.id ? (
          hasAnyClinicalRole ? (
            <StatsApiContext.Provider value={statsApi}>
              <PractitionerReportInsightsPanel
                clinicBrandId={brand?.id}
                clinicLocationId={clinicLocation?.id}
                mentorProfileData={practitioner}
                rolesOverride={roles}
              />
            </StatsApiContext.Provider>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                This practitioner has no KYC-approved clinical services (consultation / therapy /
                screening), so there are no report insights to show.
              </Typography>
            </Box>
          )
        ) : null}
      </Box>
    </Drawer>
  )
}
